/** CMS-editable Domain Redirection Policy for /domain-redirection-policy. */

export const DOMAIN_SECTION_ICON_OPTIONS = [
  { value: "file", label: "Document / Overview" },
  { value: "info", label: "Info" },
  { value: "link", label: "Link / Comparison" },
  { value: "warning", label: "Warning / Consequences" },
  { value: "headset", label: "Need Help / Support" },
];

export const DOMAIN_HERO_TITLE = "Domain Redirection Policy";

export const DOMAIN_HERO_INTRO =
  "Trocanet does not allow domain redirections that can mislead users, bypass our platform, or violate our terms. This policy explains what is allowed and what is not.";

export const DOMAIN_CTA = {
  title: "We are committed to maintaining a safe and transparent marketplace.",
  subtitle:
    "Thank you for helping us maintain trust and security for all users on Trocanet.",
};

export const DEFAULT_ALLOWED_ITEMS = [
  "Official company websites",
  "Approved brand/product pages",
  "Verified landing pages",
];

export const DEFAULT_PROHIBITED_ITEMS = [
  "Redirection to competitor websites",
  "Malware or phishing pages",
  "Adult or illegal content",
  "URL shorteners that hide the destination",
  "Any redirection that bypasses the Trocanet platform",
];

export const DEFAULT_DOMAIN_SECTIONS = [
  {
    id: "overview",
    title: "Overview",
    summary:
      "This Domain Redirection Policy applies to all users of Trocanet, including buyers, sellers and partners.",
    body: `<p>This Domain Redirection Policy applies to all users of Trocanet, including buyers, sellers and partners. It is designed to ensure a safe, transparent and trustworthy experience for everyone.</p>`,
    icon: "file",
    layout: "text",
  },
  {
    id: "what-is-domain-redirection",
    title: "What is Domain Redirection?",
    summary:
      "Domain redirection means that a domain or URL automatically sends users to another website.",
    body: `<p>Domain redirection means that a domain or URL automatically sends users to another website. This can happen through redirects, forwarding settings or third-party tools.</p>`,
    icon: "info",
    layout: "text",
  },
  {
    id: "allowed-prohibited",
    title: "Allowed & Prohibited Redirections",
    summary:
      "Trocanet allows only genuine, relevant and secure redirections. Any redirects that mislead users or create risk are not allowed.",
    body: `<p>Trocanet allows only genuine, relevant and secure redirections. Any redirects that mislead users or create risk are not allowed.</p>`,
    icon: "link",
    layout: "comparison",
    allowedTitle: "Allowed Redirections",
    prohibitedTitle: "Prohibited Redirections",
    allowedItems: [...DEFAULT_ALLOWED_ITEMS],
    prohibitedItems: [...DEFAULT_PROHIBITED_ITEMS],
  },
  {
    id: "consequences-of-violation",
    title: "Consequences of Violation",
    summary:
      "Violations may result in listing removal, account suspension or permanent termination.",
    body: `<p>If a user or business violates this Domain Redirection Policy, Trocanet may take action including listing removal, account suspension or permanent termination.</p>`,
    icon: "warning",
    layout: "text",
  },
  {
    id: "need-help",
    title: "Need Help?",
    summary: "Contact our support team for questions or to report a violation.",
    body: `<p>If you have questions about this Domain Redirection Policy or need to report a violation, please contact our support team.</p>`,
    icon: "headset",
    layout: "help",
    supportEmail: "support@trocanet.com",
    contactButtonLabel: "Contact Support",
    contactButtonLink: "/contact",
  },
];

export const DEFAULT_DOMAIN_CMS_CONFIG = {
  schemaVersion: 2,
  heroTitle: DOMAIN_HERO_TITLE,
  heroBackgroundUrl: "",
  intro: DOMAIN_HERO_INTRO,
  ctaTitle: DOMAIN_CTA.title,
  ctaSubtitle: DOMAIN_CTA.subtitle,
  sections: DEFAULT_DOMAIN_SECTIONS.map((s) => ({
    ...s,
    allowedItems: s.allowedItems ? [...s.allowedItems] : undefined,
    prohibitedItems: s.prohibitedItems ? [...s.prohibitedItems] : undefined,
    visible: true,
  })),
};

function isPlainObject(value) {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function slugify(value, fallback) {
  const slug = String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return slug || fallback;
}

function normalizeStringList(value, fallback = []) {
  if (!Array.isArray(value)) return [...fallback];
  const items = value.map((item) => String(item ?? "").trim()).filter(Boolean);
  return items.length ? items : [...fallback];
}

function mergeSections(defaults, incoming) {
  const byId = new Map();
  if (Array.isArray(incoming)) {
    for (const item of incoming) {
      if (!isPlainObject(item)) continue;
      const id = slugify(item.id || item.title, "");
      if (id) byId.set(id, item);
    }
  }
  const allowedIcons = new Set(DOMAIN_SECTION_ICON_OPTIONS.map((o) => o.value));
  return defaults.map((base) => {
    const row = byId.get(base.id) || {};
    const iconValue = String(row.icon ?? base.icon ?? "file").trim();
    const merged = {
      id: base.id,
      title: String(row.title ?? base.title ?? "").trim() || base.title,
      summary: String(row.summary ?? base.summary ?? "").trim() || base.summary,
      body: String(row.body ?? base.body ?? "").trim() || base.body,
      icon: allowedIcons.has(iconValue) ? iconValue : base.icon || "file",
      layout: base.layout || "text",
      visible: row.visible !== false,
    };

    if (base.layout === "comparison") {
      merged.allowedTitle =
        String(row.allowedTitle ?? base.allowedTitle ?? "").trim() || base.allowedTitle;
      merged.prohibitedTitle =
        String(row.prohibitedTitle ?? base.prohibitedTitle ?? "").trim() ||
        base.prohibitedTitle;
      merged.allowedItems = normalizeStringList(row.allowedItems, base.allowedItems);
      merged.prohibitedItems = normalizeStringList(
        row.prohibitedItems,
        base.prohibitedItems,
      );
    }

    if (base.layout === "help") {
      merged.supportEmail =
        String(row.supportEmail ?? base.supportEmail ?? "").trim() || base.supportEmail;
      merged.contactButtonLabel =
        String(row.contactButtonLabel ?? base.contactButtonLabel ?? "").trim() ||
        base.contactButtonLabel;
      merged.contactButtonLink =
        String(row.contactButtonLink ?? base.contactButtonLink ?? "").trim() ||
        base.contactButtonLink;
    }

    return merged;
  });
}

function cloneDefaultConfig() {
  return {
    ...DEFAULT_DOMAIN_CMS_CONFIG,
    sections: DEFAULT_DOMAIN_CMS_CONFIG.sections.map((s) => ({
      ...s,
      allowedItems: s.allowedItems ? [...s.allowedItems] : undefined,
      prohibitedItems: s.prohibitedItems ? [...s.prohibitedItems] : undefined,
    })),
  };
}

export function parseDomainCmsConfig(content) {
  if (!content || typeof content !== "string") {
    return cloneDefaultConfig();
  }

  const trimmed = content.trim();
  if (!trimmed.startsWith("{")) {
    return cloneDefaultConfig();
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (!isPlainObject(parsed)) {
      return cloneDefaultConfig();
    }

    const defaults = DEFAULT_DOMAIN_CMS_CONFIG;
    const schemaVersion = Number(parsed.schemaVersion) === 2 ? 2 : 1;

    // Old 10-section schema → remap to the new 5-section defaults.
    if (schemaVersion !== 2) {
      return {
        ...cloneDefaultConfig(),
        heroBackgroundUrl: String(
          parsed.heroBackgroundUrl ?? defaults.heroBackgroundUrl,
        ).trim(),
      };
    }

    return {
      schemaVersion: 2,
      heroTitle: defaults.heroTitle,
      heroBackgroundUrl: String(
        parsed.heroBackgroundUrl ?? defaults.heroBackgroundUrl,
      ).trim(),
      intro: defaults.intro,
      ctaTitle: defaults.ctaTitle,
      ctaSubtitle: defaults.ctaSubtitle,
      sections: mergeSections(defaults.sections, parsed.sections),
    };
  } catch {
    return cloneDefaultConfig();
  }
}

export function serializeDomainCmsConfig(config) {
  const defaults = DEFAULT_DOMAIN_CMS_CONFIG;
  const source = isPlainObject(config) ? config : defaults;
  const parsed = parseDomainCmsConfig(
    JSON.stringify({
      schemaVersion: 2,
      heroBackgroundUrl: source.heroBackgroundUrl,
      sections: source.sections,
    }),
  );
  return JSON.stringify(
    {
      schemaVersion: 2,
      heroTitle: defaults.heroTitle,
      heroBackgroundUrl: parsed.heroBackgroundUrl,
      intro: defaults.intro,
      ctaTitle: defaults.ctaTitle,
      ctaSubtitle: defaults.ctaSubtitle,
      sections: parsed.sections,
    },
    null,
    2,
  );
}

export function domainConfigFromCmsRecord(record) {
  return parseDomainCmsConfig(record?.content);
}

export function domainCmsPayloadFromConfig(config) {
  const content = serializeDomainCmsConfig(config);
  const data = parseDomainCmsConfig(content);
  return {
    title: data.heroTitle || DOMAIN_HERO_TITLE,
    description: (data.intro || "").slice(0, 200),
    content,
  };
}

export function buildDomainPageModel(page) {
  const config = parseDomainCmsConfig(page?.content);
  const sections = (config.sections?.length ? config.sections : DEFAULT_DOMAIN_SECTIONS).filter(
    (section) => section.visible !== false,
  );
  return {
    title: config.heroTitle || DOMAIN_HERO_TITLE,
    intro: config.intro || DOMAIN_HERO_INTRO,
    heroBackgroundUrl: config.heroBackgroundUrl || "",
    effectiveDate: page?.effectiveDate || null,
    updatedAt: page?.updatedAt || null,
    cta: {
      title: config.ctaTitle || DOMAIN_CTA.title,
      subtitle: config.ctaSubtitle || DOMAIN_CTA.subtitle,
    },
    sections,
    fromCms: Boolean(page?.content && String(page.content).trim().startsWith("{")),
  };
}
